import { useState } from "react";
import { ChevronDown, ChevronUp, Percent } from "lucide-react";
import { fmtCurrency } from "@/lib/api";
import HelpButton from "@/components/HelpButton";

const pct = (r) => `${(Number(r || 0) * 100).toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;

export const CATEGORY_LABELS = { revenue: "Inkomsten", opex: "Bedrijfskosten", other_income: "Overige inkomsten" };

// Verantwoording voor de kascommissie: welk inflatiepercentage is per subpost gebruikt.
export default function SubpostGrowthTable({ t, used }) {
  const [open, setOpen] = useState(true);
  const items = used?.items || [];
  if (items.length === 0) return null;
  const defaults = used.defaults || {};
  const own = items.filter((i) => i.source === "subpost").length;

  return (
    <div className="bg-card border-2 border-foreground heavy-shadow mb-8" data-testid="subpost-growth-card">
      <div className="p-6 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="stat-label">{t("Verantwoording aannames")}</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Gebruikte inflatie per subpost")}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t("{own} van {n} subposten met eigen percentage; de rest volgt de categorie-standaard.").replace("{own}", own).replace("{n}", items.length)}
            {used.baseline_year ? ` ${t("Basisbedragen: boekjaar {y}.").replace("{y}", used.baseline_year)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton section="inflatie-per-subpost" label="Uitleg inflatie per subpost" size="sm" />
          <button onClick={() => setOpen(!open)} data-testid="subpost-growth-toggle" className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {open ? t("Inklappen") : t("Tonen")}
          </button>
        </div>
      </div>
      {open && (
        <div className="overflow-x-auto border-t-2 border-foreground">
          <table className="w-full text-sm" data-testid="subpost-growth-table">
            <thead className="bg-foreground text-background">
              <tr>
                {[t("Categorie"), t("Subpost"), t("Basisbedrag"), t("Inflatie"), t("Bron")].map((h, i) => (
                  <th key={h} className={`px-4 py-2 font-display uppercase text-[10px] tracking-widest ${i === 2 || i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {["revenue", "opex", "other_income"].flatMap((cat) => items.filter((i) => i.category === cat).map((it, idx) => (
                <tr key={`${cat}-${it.name}`} className="border-t border-border even:bg-muted/30" data-testid={`subpost-growth-row-${cat}-${it.name}`}>
                  <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{idx === 0 ? t(CATEGORY_LABELS[cat]) : ""}</td>
                  <td className="px-4 py-2 font-mono-num">{it.name}</td>
                  <td className="px-4 py-2 font-mono-num text-right">{fmtCurrency(it.base_amount)}</td>
                  <td className={`px-4 py-2 font-mono-num text-right ${it.source === "subpost" ? "font-bold" : ""}`}>{pct(it.rate)}</td>
                  <td className="px-4 py-2 text-xs whitespace-nowrap">
                    {it.source === "subpost"
                      ? <span className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-0.5 font-display uppercase text-[10px] tracking-widest"><Percent className="w-3 h-3" /> {t("eigen percentage")}</span>
                      : <span className="text-muted-foreground">{t("standaard {cat}").replace("{cat}", t(CATEGORY_LABELS[cat]).toLowerCase())} ({pct(defaults[cat])})</span>}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
          <div className="px-4 py-3 text-xs text-muted-foreground border-t border-border flex flex-wrap gap-x-6 gap-y-1" data-testid="subpost-growth-defaults">
            <span>{t("Standaard")}: {t("Inkomsten")} <b className="font-mono-num">{pct(defaults.revenue)}</b></span>
            <span>{t("Bedrijfskosten")} <b className="font-mono-num">{pct(defaults.opex)}</b></span>
            <span>{t("Overige inkomsten")} <b className="font-mono-num">{pct(defaults.other_income)}</b></span>
            <span>{t("Algemene inflatie")} <b className="font-mono-num">{pct(defaults.inflation)}</b></span>
          </div>
        </div>
      )}
    </div>
  );
}
